import React from 'react';
import { useTranslation } from 'react-i18next';
import { Quote, ChildQuote, QuoteJob, QuoteTask, QuotePricingType } from '../../types/quote';
import ProfileAvatar from '../common/ProfileAvatar';
import { twMerge } from 'tailwind-merge';
import FormattedCurrency from '../common/FormattedCurrency';
import RatingStars from '../common/RatingStars';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import AlliancesHeadshots from '../alliances/AlliancesHeadshots';
import { computeQuoteAmount } from '../../utils/jobUtils';
import { formatDateLocalized } from '../../utils/dateUtils';
import { Job, JobQuote } from '../../types/job';
import { useGetFreelancerOverallRating } from '../../hooks/useGetFreelancerOverallRating';
import { Link } from 'react-router-dom';
import { Button } from '../common/Button';
import ProfileBioText from '../common/ProfileBioText';

const TASK_TYPES_WITH_QUANTITY: QuotePricingType[] = ['hourly', 'unit'];

function taskLineTotal(task: QuoteTask): number {
  if (TASK_TYPES_WITH_QUANTITY.includes(task.pricing_type)) {
    return Number(task.quantity ?? 0) * Number(task.unit_price ?? 0);
  }
  return Number(task.unit_price ?? 0);
}

interface TaskLineRowProps {
  task: QuoteTask;
  t: (key: string, options?: { ns?: string; defaultValue?: string }) => string;
}

function TaskLineRow({ task, t }: TaskLineRowProps) {
  const quantityVisible = TASK_TYPES_WITH_QUANTITY.includes(task.pricing_type);
  const rowBorder = 'border-b border-[#E1E9F0]/50 pb-2';
  return (
    <>
      <div className={`text-sm text-secondary ${rowBorder}`}>{task.name || '—'}</div>
      <div className={`text-sm text-secondary ${rowBorder}`}>
        {t(`createQuoteModal.pricingType.${task.pricing_type}`, { ns: 'quotes' })}
      </div>
      <div className={`text-sm text-secondary ${rowBorder}`}>
        {quantityVisible ? task.quantity : '—'}
      </div>
      <div className={`text-sm text-secondary ${rowBorder}`}>
        <FormattedCurrency value={task.unit_price} />
      </div>
      <div className={`flex min-h-[2.5rem] items-center font-semibold text-primary ${rowBorder}`}>
        <FormattedCurrency value={task.total_amount ?? taskLineTotal(task)} />
      </div>
    </>
  );
}

function TaskLineCard({ task, t }: TaskLineRowProps) {
  const quantityVisible = TASK_TYPES_WITH_QUANTITY.includes(task.pricing_type);
  const labelClass =
    'block font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] text-secondary mb-1';
  return (
    <div className="grid gap-4 py-2 border-b border-[#E1E9F0]/50 grid-cols-1">
      <div>
        <span className={labelClass}>
          {t('createQuoteModal.taskDescriptionColumnHeader', {
            ns: 'quotes',
            defaultValue: 'Description de la tâche',
          })}
        </span>
        <span className="text-sm text-secondary">{task.name || '—'}</span>
      </div>
      <div>
        <span className={labelClass}>
          {t('createQuoteModal.typeColumnHeader', { ns: 'quotes', defaultValue: 'Type' })}
        </span>
        <span className="text-sm text-secondary">
          {t(`createQuoteModal.pricingType.${task.pricing_type}`, { ns: 'quotes' })}
        </span>
      </div>
      <div>
        <span className={labelClass}>
          {t('createQuoteModal.quantityColumnHeader', {
            ns: 'quotes',
            defaultValue: 'Quantité',
          })}
        </span>
        <span className="text-sm text-secondary">{quantityVisible ? task.quantity : '—'}</span>
      </div>
      <div>
        <span className={labelClass}>
          {t('createQuoteModal.unitPriceColumnHeader', {
            ns: 'quotes',
            defaultValue: 'Prix unitaire',
          })}
        </span>
        <span className="text-sm text-secondary">
          <FormattedCurrency value={task.unit_price} />
        </span>
      </div>
      <div>
        <span className={labelClass}>
          {t('createQuoteModal.totalLabel', { ns: 'quotes', defaultValue: 'Total' })}
        </span>
        <div className="flex min-h-[2.5rem] items-center font-semibold text-primary">
          <FormattedCurrency value={task.total_amount ?? taskLineTotal(task)} />
        </div>
      </div>
    </div>
  );
}

interface QuoteDetailsInfoProps {
  quote: Quote | ChildQuote | JobQuote;
  job: QuoteJob | Job;
  headerOnly?: boolean;
  detailsOnly?: boolean;
  isProject?: boolean;
  className?: string;
  avatarClassName?: string;
  title?: string;
  subTotalLabel?: string;
  variant?: 'offer' | 'quote';
}

export const QuoteDetailsInfo: React.FC<QuoteDetailsInfoProps> = ({
  quote,
  headerOnly = false,
  detailsOnly = false,
  isProject = false,
  className,
  avatarClassName,
  title,
  subTotalLabel,
  variant = 'quote',
}) => {
  const { t, i18n } = useTranslation([
    'quotes',
    'common',
    'freelancers',
    'settings',
    'jobs',
    'alliances',
  ]);

  const { loading: rateLoading, rating: freelancerRating } = useGetFreelancerOverallRating({
    freelancerId: quote.quote_owner.id,
  });

  const quoteOwner = quote.quote_owner;
  if (!quoteOwner) return null;

  // Calculate total amount from quote tasks
  const quoteAmount = computeQuoteAmount(quote);

  const teamMembers =
    'alliances' in quote
      ? quote.alliances
          .filter(alliance => alliance.child_quote)
          .map(alliance => alliance.child_quote!)
      : [];

  const shouldRenderQuoteOwnerBioAndProfileButton =
    (variant === 'quote' && !detailsOnly && !isProject) || (variant === 'offer' && !headerOnly);

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const formattedSubmissionDate = quote.submitted_at
    ? formatDateLocalized(quote.submitted_at, i18n.language, dateFormatOptions)
    : '';

  return (
    <div className="space-y-6">
      {/* Header with freelancer info */}
      {!detailsOnly && (
        <div
          className={twMerge(
            `flex flex-col gap-4 w-full md:grid md:grid-cols-[6.25rem_auto_auto] md:items-center md:gap-x-8`,
            className
          )}
        >
          <ProfileAvatar
            profile_id={quoteOwner.id}
            first_name={quoteOwner.first_name}
            avatar_url={quoteOwner.avatar_url}
            className={twMerge(
              'flex-shrink-0 self-start w-16 h-16 md:self-center md:w-24 md:h-24',
              avatarClassName
            )}
          />

          <div className="text[1.5625rem] text-secondary self-center font-bold text-2xl md:text-lg leading-tight">
            {quoteOwner.first_name} {quoteOwner.last_name}
          </div>

          <div className="w-full md:col-span-1 md:grid-cols-none md:flex md:items-start justify-between md:space-x-5">
            {/* Services */}
            <div className="flex flex-col mt-4 w-full">
              <span className="text-sm md:text-xs whitespace-nowrap md:mb-3 text-secondary">
                {t('servicesOffered', { ns: 'settings' })}
              </span>
              <span className="text-sm text-secondary md:mt-0 mt-1">
                {getFreelancerSubtitle(
                  {
                    service_category_slugs: quoteOwner?.services?.map(service => service.slug),
                  },
                  t
                )}
              </span>
            </div>

            {formattedSubmissionDate && (
              <div className="flex flex-col mt-4 w-full">
                <span className="text-sm md:text-xs whitespace-nowrap md:mb-3 text-secondary">
                  {t('quoteRequestInfo.submittedDate', { ns: 'quotes' })}
                </span>
                <span className="text-sm text-secondary font-bold md:mt-0 mt-1">
                  {formattedSubmissionDate}
                </span>
              </div>
            )}

            <div className="flex flex-row items-center justify-between mt-4 w-full">
              <div className="flex flex-col items-start">
                <span className="text-sm md:text-xs whitespace-nowrap mb-3 text-secondary">
                  {t('distance', { ns: 'freelancers', defaultValue: 'Distance' })}
                </span>
                <span className="text-sm text-secondary font-bold">-</span>
              </div>

              {/* Évaluation */}
              <div className="flex flex-col items-start">
                <span className="text-sm md:text-xs whitespace-nowrap mb-3 text-secondary">
                  {t('rating', { ns: 'freelancers', defaultValue: 'Évaluation' })}
                </span>
                <span className="text-sm text-secondary font-bold">
                  <RatingStars loading={rateLoading} rating={freelancerRating} />
                </span>
              </div>
            </div>

            {variant === 'offer' && quoteAmount > 0 && (
              <div className="flex flex-col items-start mt-4">
                <span className="text-sm md:text-xs text-secondary whitespace-nowrap mb-3">
                  {subTotalLabel || t('sub_total', { ns: 'quotes' })}
                </span>
                {quoteAmount === 0 ? (
                  <span className="text-sm text-secondary">{t('childQuoteOfferPending')}</span>
                ) : (
                  <span className="text-lg text-primary font-bold">
                    <FormattedCurrency value={quoteAmount!} />
                  </span>
                )}
              </div>
            )}

            {/* Alliance or Team Members */}
            {variant === 'quote' && teamMembers && teamMembers.length > 0 && (
              <>
                <div className="flex flex-col items-start mt-4">
                  <span className="text-sm md:text-xs text-[#304659] whitespace-nowrap mb-3">
                    {isProject
                      ? t('teamMembers', { ns: 'quotes', defaultValue: 'Équipe' })
                      : t('alliances', { ns: 'alliances', defaultValue: 'Alliances' })}
                  </span>
                  <AlliancesHeadshots
                    alliances={teamMembers.map(childQuote => ({
                      profile_id: childQuote.quote_owner.id,
                      first_name: childQuote.quote_owner.first_name,
                      avatar_url: childQuote.quote_owner.avatar_url || '',
                      status: 'accepted',
                    }))}
                    allianceCount={teamMembers.length}
                    avatarSize="w-5 h-5"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {shouldRenderQuoteOwnerBioAndProfileButton && (
        <>
          {quote.quote_owner?.bio && (
            <div className="mt-8">
              <h4 className={`font-bold text-lg text-primary`}>
                {t('about', { ns: 'freelancers', defaultValue: 'À propos' })}
              </h4>
              <p className="text-sm text-secondary" data-testid="freelancer-bio">
                <ProfileBioText
                  bio={quote.quote_owner.bio}
                  profileId={quote.quote_owner.id}
                  skipRedaction={true}
                />
              </p>
            </div>
          )}

          <div className="flex justify-end items-center mt-4 md:mt-0">
            <Link to={`/freelancers/${quote.quote_owner.id}`}>
              <Button
                variant="transparent-with-border"
                className="inline-flex !text-base md:!text-sm px-6 py-3 md:px-4 md:py-2 rounded-full "
              >
                {t('viewProfile', { ns: 'freelancers' })}
              </Button>
            </Link>
          </div>
        </>
      )}

      {shouldRenderQuoteOwnerBioAndProfileButton && !headerOnly && (
        <hr className="md:hidden border-[#E1E9F0] mt-5 mb-2" />
      )}

      {/* Details content */}
      {!headerOnly && (
        <>
          {/* TASKS (pricing lines) – read-only grid like OfferLinesEditor */}
          {quote.tasks && quote.tasks.length > 0 && (
            <div className="md:mt-4">
              <h4 className="font-bold text-lg text-secondary mb-3">
                {title || t('tasks', { ns: 'quotes' })}
              </h4>
              {/* Desktop: single grid, same column layout as OfferLinesEditor (no actions column) */}
              <div className="hidden md:grid gap-x-4 gap-y-2 px-1 grid-cols-[3fr_1fr_1fr_1fr_1fr] items-center">
                <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
                  {t('createQuoteModal.taskDescriptionColumnHeader', {
                    ns: 'quotes',
                    defaultValue: 'Description de la tâche',
                  })}
                </span>
                <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
                  {t('createQuoteModal.typeColumnHeader', { ns: 'quotes', defaultValue: 'Type' })}
                </span>
                <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
                  {t('createQuoteModal.quantityColumnHeader', {
                    ns: 'quotes',
                    defaultValue: 'Quantité',
                  })}
                </span>
                <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
                  {t('createQuoteModal.unitPriceColumnHeader', {
                    ns: 'quotes',
                    defaultValue: 'Prix unitaire',
                  })}
                </span>
                <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
                  {t('createQuoteModal.totalLabel', { ns: 'quotes', defaultValue: 'Total' })}
                </span>
                {quote.tasks.map((task, idx) => (
                  <TaskLineRow key={task.id ?? idx} task={task} t={t} />
                ))}
              </div>
              {/* Mobile: stacked card per line */}
              <div className="md:hidden space-y-4">
                {quote.tasks.map((task, idx) => (
                  <TaskLineCard key={task.id ?? idx} task={task} t={t} />
                ))}
              </div>
            </div>
          )}
          {/* Change request message (recruiter/principal requested adjustments) */}
          {'change_request_message' in quote &&
            quote.change_request_message &&
            quote.change_request_message.trim() !== '' && (
              <div className="md:mt-4">
                <h4 className="font-bold text-lg text-secondary">
                  {t('createQuoteModal.changeRequestLabel', 'Demande d’ajustement')}
                </h4>
                <p className="text-sm text-secondary whitespace-pre-wrap mt-1">
                  {quote.change_request_message}
                </p>
              </div>
            )}
          {/* Offer-level notes */}
          {'message' in quote && quote.message && quote.message.trim() !== '' && (
            <div className="md:mt-4">
              <h4 className="font-bold text-lg text-secondary">
                {t('createQuoteModal.additionalNotesLabel', { ns: 'quotes' })}
              </h4>
              <p className="text-sm text-secondary whitespace-pre-wrap mt-1">{quote.message}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
